import React, { useState } from "react";
import { alpha, Box, ButtonBase, Tooltip, Typography } from "@mui/material";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import CustomButton from "./common/CustomButton";
import { COLORS } from "../assets/theme";

const recentRangeOptions = ["Last 1m", "Last 10m", "Last 30m"];

const PILL_HEIGHT = 36;
const PILL_WIDTH = { xs: 96, sm: 104 };
const STEPPER_WIDTH = { xs: 124, sm: 136 };

interface DateFilterButtonsProps {
  /** Receives the "from_to" range, plus the label of the button that produced it. */
  onSelect: (selectedRange: string, selectedLabel: string) => void;
  selectedOption?: string;
  fromDate?: Date | null;
  toDate?: Date | null;
}

const MONTH_SHORT = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const pad = (n: number) => String(n).padStart(2, "0");

const fmt = (d: Date, includeTime: boolean = false) => {
  const year = d.getUTCFullYear();
  const month = pad(d.getUTCMonth() + 1);
  const day = pad(d.getUTCDate());
  if (includeTime) {
    const hours = pad(d.getUTCHours());
    const minutes = pad(d.getUTCMinutes());
    const seconds = pad(d.getUTCSeconds());
    return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}.000Z`;
  }
  return `${year}-${month}-${day}T00:00:00.000Z`;
};

/** The calendar today/this-month an offset is counted from. Always UTC, since
 *  the backend is - a local anchor would roll over to the next day early in
 *  any timezone ahead of UTC. */
interface Anchor {
  year: number;
  month: number;
  day: number;
}

const utcAnchor = (): Anchor => {
  const now = new Date();
  return {
    year: now.getUTCFullYear(),
    month: now.getUTCMonth(),
    day: now.getUTCDate(),
  };
};

// --- Day stepper: offset in days from today (0 = Today, capped - no future dates) ---

const dayRangeFrom = (anchor: Anchor, offset: number): string => {
  const { year, month, day } = anchor;
  const start = new Date(Date.UTC(year, month, day + offset));
  const end = new Date(Date.UTC(year, month, day + offset + 1));
  return `${fmt(start)}_${fmt(end)}`;
};

const getDayRange = (offset: number): string =>
  dayRangeFrom(utcAnchor(), offset);

const getDayLabel = (offset: number): string => {
  if (offset === 0) return "Today";
  if (offset === -1) return "Yesterday";
  const { year, month, day } = utcAnchor();
  const d = new Date(Date.UTC(year, month, day + offset));
  return `${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCDate()}`;
};

// --- Month stepper: offset in months from this month (0 = This Month, capped - no future months) ---

const monthRangeFrom = (anchor: Anchor, offset: number): string => {
  const { year, month } = anchor;
  const start = new Date(Date.UTC(year, month + offset, 1));
  const end = new Date(Date.UTC(year, month + offset + 1, 1));
  return `${fmt(start)}_${fmt(end)}`;
};

const getMonthRange = (offset: number): string =>
  monthRangeFrom(utcAnchor(), offset);

const getMonthLabel = (offset: number): string => {
  if (offset === 0) return "This Month";
  if (offset === -1) return "Last Month";
  const { year, month } = utcAnchor();
  const d = new Date(Date.UTC(year, month + offset, 1));
  return `${MONTH_SHORT[d.getUTCMonth()]} ${d.getUTCFullYear()}`;
};

// --- "Last Xm" quick ranges (unchanged) ---

const RECENT_MINUTES: Record<string, number> = {
  "Last 1m": 1,
  "Last 10m": 10,
  "Last 30m": 30,
};

const getRecentRange = (option: string): string => {
  const minutes = RECENT_MINUTES[option];
  if (!minutes) return "";
  const now = new Date();
  const start = new Date(now.getTime() - minutes * 60 * 1000);
  return `${fmt(start, true)}_${fmt(now, true)}`;
};

/** Whether a "start_end" range string exactly matches the currently applied
 *  fromDate/toDate. */
const rangeMatchesDates = (
  range: string,
  fromDate?: Date | null,
  toDate?: Date | null,
): boolean => {
  if (!fromDate || !toDate || !range) return false;
  const [startStr, endStr] = range.split("_");
  return (
    new Date(startStr).getTime() === fromDate.getTime() &&
    new Date(endStr).getTime() === toDate.getTime()
  );
};

const appliedIsDayOffset = (
  offset: number,
  fromDate?: Date | null,
  toDate?: Date | null,
): boolean =>
  rangeMatchesDates(dayRangeFrom(utcAnchor(), offset), fromDate, toDate);

const appliedIsMonthOffset = (
  offset: number,
  fromDate?: Date | null,
  toDate?: Date | null,
): boolean =>
  rangeMatchesDates(monthRangeFrom(utcAnchor(), offset), fromDate, toDate);

type ActiveKind = "day" | "month" | "recent" | "";

interface ResolvedSelection {
  kind: ActiveKind;
  dayOffset?: number;
  monthOffset?: number;
  recentLabel?: string;
}

/** Maps a label previously emitted by this component back to the stepper
 *  state it came from, so a controlled `selectedOption` (e.g. a page
 *  restoring a saved selection) re-selects the right button. */
const resolveSelection = (label: string): ResolvedSelection => {
  if (label === "") return { kind: "" };
  if (recentRangeOptions.includes(label))
    return { kind: "recent", recentLabel: label };
  switch (label) {
    case "Today":
      return { kind: "day", dayOffset: 0 };
    case "Yesterday":
      return { kind: "day", dayOffset: -1 };
    case "This Month":
      return { kind: "month", monthOffset: 0 };
    case "Last Month":
      return { kind: "month", monthOffset: -1 };
    default:
      return { kind: "" };
  }
};

/** What clicking the label in the middle of a stepper does:
 *  - "select": applies the value it already shows. That's how you get back
 *    onto it after something else (e.g. "Last 30m") took the selection away,
 *    without having to step off it and back again.
 *  - "reset": the pill is the active filter but has been stepped away from
 *    the latest value, so clicking jumps straight back to today / this month
 *    instead of walking the arrow back N times.
 *  The label is always a click target; it costs the two arrow halves the
 *  width it covers in the middle of the pill. */
type LabelMode = "select" | "reset";

interface DateStepperProps {
  label: string;
  isSelected: boolean;
  canStepForward: boolean;
  labelMode: LabelMode;
  labelHint: string;
  onStepBack: () => void;
  onStepForward: () => void;
  onLabelClick: () => void;
  ariaLabel: string;
}

const DateStepper: React.FC<DateStepperProps> = ({
  label,
  isSelected,
  canStepForward,
  labelMode,
  labelHint,
  onStepBack,
  onStepForward,
  onLabelClick,
  ariaLabel,
}) => {
  const contentColor = isSelected ? COLORS.iconColor : COLORS.placeHolderColor;
  const halfSx = {
    flex: 1,
    height: "100%",
    display: "flex",
    alignItems: "center",
    color: contentColor,
    "&:hover": {
      backgroundColor: alpha(COLORS.GOLD, isSelected ? 0.25 : 0.12),
    },
  };
  const labelSx = {
    fontSize: { xs: "0.7rem", sm: "0.75rem" },
    fontWeight: isSelected ? 700 : 500,
    color: contentColor,
    whiteSpace: "nowrap",
  };

  return (
    <Box
      display="flex"
      alignItems="center"
      sx={{
        position: "relative",
        border: `1px solid ${COLORS.GOLD}`,
        borderRadius: "20px",
        height: PILL_HEIGHT,
        minHeight: PILL_HEIGHT,
        width: STEPPER_WIDTH,
        minWidth: STEPPER_WIDTH,
        backgroundColor: isSelected ? COLORS.GOLD : "transparent",
        overflow: "hidden",
        flex: "0 0 auto",
      }}
    >
      {/* Each half of the pill is one big touch target - click anywhere on
          the left of it to step back, anywhere on the right to step forward.
          The label sits on top and passes clicks through to the half under it. */}
      <ButtonBase
        onClick={onStepBack}
        aria-label={`${ariaLabel}, back`}
        sx={{
          ...halfSx,
          justifyContent: "flex-start",
          pl: { xs: 0.75, sm: 1 },
        }}
      >
        <ArrowBackIosNewIcon sx={{ fontSize: 11 }} />
      </ButtonBase>
      <ButtonBase
        disabled={!canStepForward}
        onClick={onStepForward}
        aria-label={`${ariaLabel}, forward`}
        sx={{
          ...halfSx,
          justifyContent: "flex-end",
          pr: { xs: 0.75, sm: 1 },
          color: canStepForward ? contentColor : "#ccc",
        }}
      >
        <ArrowForwardIosIcon sx={{ fontSize: 11 }} />
      </ButtonBase>
      {/* The middle is its own target: it applies the value shown, or jumps
          back to the latest one once the pill has been stepped away. */}
      <Tooltip title={labelHint} arrow>
        <ButtonBase
          onClick={onLabelClick}
          aria-label={labelHint}
          sx={{
            position: "absolute",
            left: "50%",
            top: "50%",
            transform: "translate(-50%, -50%)",
            px: 0.75,
            py: 0.25,
            borderRadius: "10px",
            "&:hover": {
              backgroundColor: alpha(COLORS.GOLD, isSelected ? 0.25 : 0.12),
            },
          }}
        >
          <Typography
            sx={{
              ...labelSx,
              textDecoration:
                labelMode === "reset" ? "underline dotted" : "none",
            }}
          >
            {label}
          </Typography>
        </ButtonBase>
      </Tooltip>
    </Box>
  );
};

const DateFilterButtons: React.FC<DateFilterButtonsProps> = ({
  onSelect,
  selectedOption,
  fromDate,
  toDate,
}) => {
  const initial = resolveSelection(selectedOption ?? "Today");
  const [dayOffset, setDayOffset] = useState(initial.dayOffset ?? 0);
  const [monthOffset, setMonthOffset] = useState(initial.monthOffset ?? 0);
  const [activeKind, setActiveKind] = useState<ActiveKind>(initial.kind);
  const [activeRecentLabel, setActiveRecentLabel] = useState<string>(
    initial.recentLabel ?? "",
  );
  const appliedRangeSeenRef = React.useRef(false);

  React.useEffect(() => {
    if (selectedOption === undefined) return;

    const resolved = resolveSelection(selectedOption);
    if (resolved.kind === "recent") {
      setActiveKind("recent");
      setActiveRecentLabel(resolved.recentLabel ?? "");
      return;
    }
    if (resolved.kind === "day") {
      setActiveKind("day");
      setDayOffset(resolved.dayOffset ?? 0);
      return;
    }
    if (resolved.kind === "month") {
      setActiveKind("month");
      setMonthOffset(resolved.monthOffset ?? 0);
      return;
    }
    if (selectedOption === "") {
      setActiveKind("");
      return;
    }
    // A custom label this component itself produced further back/forward
    // (e.g. restored verbatim after a page's own "Live" mode toggle) -
    // re-select whichever stepper's current value still matches it,
    // without losing its offset.
    if (selectedOption === getDayLabel(dayOffset)) {
      setActiveKind("day");
    } else if (selectedOption === getMonthLabel(monthOffset)) {
      setActiveKind("month");
    } else {
      setActiveKind("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedOption]);

  React.useEffect(() => {
    if (fromDate === undefined && toDate === undefined) return;

    if (!fromDate || !toDate) {
      // No range applied. Before the first real one lands this only means the
      // page hasn't applied its default yet (and under StrictMode this effect
      // runs twice before that happens), so leave the highlight alone.
      // Afterwards it means the range was cleared, so drop it.
      if (
        appliedRangeSeenRef.current &&
        (activeKind === "day" || activeKind === "month")
      ) {
        setActiveKind("");
      }
      return;
    }
    appliedRangeSeenRef.current = true;

    // Keep the highlight a function of what's actually applied: whichever
    // stepper describes the applied range owns it, and if neither does (a
    // custom pick) no stepper stays lit. "Last Xm" tracks its own selection -
    // its window never matches a whole day or month - so leave it be.
    if (appliedIsDayOffset(dayOffset, fromDate, toDate)) {
      if (activeKind !== "day") setActiveKind("day");
    } else if (appliedIsMonthOffset(monthOffset, fromDate, toDate)) {
      if (activeKind !== "month") setActiveKind("month");
    } else if (activeKind === "day" || activeKind === "month") {
      setActiveKind("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate]);

  const selectDay = (offset: number) => {
    setDayOffset(offset);
    setActiveKind("day");
    onSelect(getDayRange(offset), getDayLabel(offset));
  };

  const selectMonth = (offset: number) => {
    setMonthOffset(offset);
    setActiveKind("month");
    onSelect(getMonthRange(offset), getMonthLabel(offset));
  };

  const stepDay = (direction: 1 | -1) => {
    if (direction === 1 && dayOffset >= 0) return; // no future dates
    selectDay(dayOffset + direction);
  };

  const stepMonth = (direction: 1 | -1) => {
    if (direction === 1 && monthOffset >= 0) return; // no future months
    selectMonth(monthOffset + direction);
  };

  const dayLabelMode: LabelMode =
    activeKind === "day" && dayOffset !== 0 ? "reset" : "select";
  const monthLabelMode: LabelMode =
    activeKind === "month" && monthOffset !== 0 ? "reset" : "select";

  const handleRecentClick = (option: string) => {
    if (activeKind === "recent" && activeRecentLabel === option) {
      setActiveKind("");
      setActiveRecentLabel("");
      onSelect("", "");
    } else {
      setActiveKind("recent");
      setActiveRecentLabel(option);
      onSelect(getRecentRange(option), option);
    }
  };

  return (
    <Box
      display="flex"
      flexWrap="wrap"
      alignItems="center"
      gap={{ xs: 0.75, sm: 1 }}
    >
      <DateStepper
        label={getDayLabel(dayOffset)}
        isSelected={activeKind === "day"}
        canStepForward={dayOffset < 0}
        labelMode={dayLabelMode}
        labelHint={
          dayLabelMode === "reset"
            ? "Back to today"
            : `Select ${getDayLabel(dayOffset)}`
        }
        onStepBack={() => stepDay(-1)}
        onStepForward={() => stepDay(1)}
        onLabelClick={() => selectDay(dayLabelMode === "reset" ? 0 : dayOffset)}
        ariaLabel="Day filter"
      />
      <DateStepper
        label={getMonthLabel(monthOffset)}
        isSelected={activeKind === "month"}
        canStepForward={monthOffset < 0}
        labelMode={monthLabelMode}
        labelHint={
          monthLabelMode === "reset"
            ? "Back to this month"
            : `Select ${getMonthLabel(monthOffset)}`
        }
        onStepBack={() => stepMonth(-1)}
        onStepForward={() => stepMonth(1)}
        onLabelClick={() =>
          selectMonth(monthLabelMode === "reset" ? 0 : monthOffset)
        }
        ariaLabel="Month filter"
      />
      {recentRangeOptions.map((option) => {
        const isSelected =
          activeKind === "recent" && activeRecentLabel === option;
        return (
          <CustomButton
            key={option}
            label={option}
            onClick={() => handleRecentClick(option)}
            bgColor={isSelected ? COLORS.GOLD : "transparent"}
            textColor={isSelected ? COLORS.iconColor : COLORS.placeHolderColor}
            borderColor={COLORS.GOLD}
            borderRadius="20px"
            sx={{
              borderWidth: 1,
              height: PILL_HEIGHT,
              minHeight: PILL_HEIGHT,
              borderStyle: "solid",
              flex: "0 0 auto",
              width: PILL_WIDTH,
              minWidth: PILL_WIDTH,
              px: { xs: 0.75, sm: 1.25 },
              fontSize: { xs: "0.7rem", sm: "0.75rem" },
              whiteSpace: "nowrap",
            }}
          />
        );
      })}
    </Box>
  );
};

export default DateFilterButtons;
